import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import userServices from "services/userServices";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const permissionFetchedRef = useRef(false);

  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [permission, setPermission] = useState({
    permissions: {},
    allowedRoutes: [],
  });

  const [loading, setLoading] = useState(true);
  const [isItemsLoading, setIsItemsLoading] = useState(false);
  const [permissionReady, setPermissionReady] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem("user");
    permissionFetchedRef.current = false;
    setUser(null);
    setRole(null);
    setPermission({ permissions: {}, allowedRoutes: [] });
    setPermissionReady(false);
    setLoading(false);
    navigate("/login");
  }, [navigate]);

  const getPermissions = useCallback(
    async (currentUser) => {
      if (!currentUser || permissionFetchedRef.current) return;

      permissionFetchedRef.current = true;
      setIsItemsLoading(true);

      try {
        const res = await userServices.getPermission(currentUser._id);
        const data = res?.data?.data || res?.data || res;

        if (data?.success || (data && !data.error && data.permissions)) {
          const allowedRoutes = Array.isArray(data.allowedRoutes)
            ? data.allowedRoutes.map((p) => p.toLowerCase().replace(/\s+/g, ""))
            : [];

          if (data?.email_verified !== undefined || data?.user?.email_verified !== undefined) {
            const freshEmailVerified = data?.email_verified ?? data?.user?.email_verified;
            setUser((prev) => {
              const updated = { ...prev, email_verified: freshEmailVerified };
              localStorage.setItem("user", JSON.stringify(updated));
              return updated;
            });
          }

          setPermission({
            permissions: data.permissions || {},
            allowedRoutes,
          });

          setRole(data.role || null);
          setPermissionReady(true);
        } else {
          console.error(data?.error || "Failed to load permissions");
          logout();
        }
      } catch (err) {
        console.error(err);
        permissionFetchedRef.current = false;
        logout();
      } finally {
        setIsItemsLoading(false);
      }
    },
    [logout]
  );

  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (!savedUser) {
      setLoading(false);
      return;
    }

    const parsedUser = JSON.parse(savedUser);
    setUser(parsedUser);
    setRole(parsedUser?.role || null);

    getPermissions(parsedUser);
    setLoading(false);
  }, [getPermissions]);

  const login = (data) => {
    const userData = data.user;
    localStorage.setItem("user", JSON.stringify(userData));
    permissionFetchedRef.current = false;

    setUser(userData);
    setRole(userData?.role || null);
    setPermission({ permissions: {}, allowedRoutes: [] });
    setPermissionReady(false);

    getPermissions(userData);
    navigate("/admin/dashboard");
  };

  const updateUser = (updatedData) => {
    if (!user) return;
    const newUser = {
      ...user,
      ...updatedData,
      accessToken: user.accessToken,
      role: user.role,
    };
    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        role,
        permission,
        permissionReady,
        loading,
        isItemsLoading,
        login,
        logout,
        updateUser,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const selectCanUpdateListings = (state) => {
  if (!state || typeof state !== "object") return false;
  const permissionObj = state.permission || state;
  const modules = permissionObj?.permissions?.modules;
  if (!modules || typeof modules !== "object") return false;
  return Boolean(modules?.listings?.update);
};
